import { useRef, useState } from "react";
import clsx from "clsx";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronUp } from "lucide-react";
import { Dialog, Popover } from "react-aria-components";
import { Button } from "ui-kit-v2/src/button/button";
import { _ } from "i18n";
import { getNextPlayerSort, PLAYER_SORT_OPTIONS, PlayerSortDirection, type PlayerSort, type PlayerSortKey } from "./sorting";
import styles from "./styles.module.scss";

interface CoachReviewsPlayerSortButtonProps {
	value: PlayerSort | null;
	onChange: (value: PlayerSort | null) => void;
}

export const CoachReviewsPlayerSortButton = ({ value, onChange }: CoachReviewsPlayerSortButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);

	const Chevron = isOpen ? ChevronUp : ChevronDown;
	const label = _("game.coachReviews.playerList.sortButton.label");

	const handleSelect = (key: PlayerSortKey) => {
		onChange(getNextPlayerSort(value, key));
		setIsOpen(false);
	};

	return (
		<>
			<div ref={triggerRef} className={styles["sort"]}>
				<Button
					size="small"
					variant="outline"
					className="w-fit"
					iconLeft={<ArrowUpDown size={16} />}
					iconRight={<Chevron size={16} />}
					aria-haspopup="dialog"
					aria-expanded={isOpen}
					onClick={() => setIsOpen((open) => !open)}
				>
					{label}
				</Button>
			</div>

			<Popover
				triggerRef={triggerRef}
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				placement="bottom end"
				offset={4}
				className={styles["sort__popover"]}
			>
				<Dialog className={styles["sort__dialog"]} aria-label={label}>
					{PLAYER_SORT_OPTIONS.map((option) => {
						const isActive = value?.key === option.key;
						const direction = isActive ? value.direction : PlayerSortDirection.Asc;
						const DirectionIcon = direction === PlayerSortDirection.Asc ? ArrowDown : ArrowUp;

						return (
							<button
								key={option.key}
								type="button"
								className={clsx(styles["sort__option"], {
									[styles["sort__option--active"]]: isActive,
								})}
								onClick={() => handleSelect(option.key)}
							>
								<span className={styles["sort__option-label"]}>{_(option.labelKey)}</span>
								<DirectionIcon size={20} aria-hidden />
							</button>
						);
					})}
				</Dialog>
			</Popover>
		</>
	);
};
